import React, { useState, useEffect } from 'react';
import { DECKS } from './data/decks';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useCustomDecks } from './hooks/useCustomDecks';
import { useSwipe } from './hooks/useSwipe';
import { useKeyboard } from './hooks/useKeyboard';
import { useStudyTimer } from './hooks/useStudyTimer';
import { useStreaks } from './hooks/useStreaks';
import { useStudyGoals } from './hooks/useStudyGoals';
import { useAchievements } from './hooks/useAchievements';
import { useDeckCategories } from './hooks/useDeckCategories';
import { Header } from './components/Header';
import { TopicSelector } from './components/TopicSelector';
import { Flashcard } from './components/Flashcard';
import { Stats } from './components/Stats';
import { BottomNav } from './components/BottomNav';
import { MemorizedModal } from './components/MemorizedModal';
import { FavoritesModal } from './components/FavoritesModal';
import { MenuSheet } from './components/MenuSheet';
import { DeckManager } from './components/DeckManager';
import { CardEditor } from './components/CardEditor';
import { CategoryManager } from './components/CategoryManager';
import { SearchBar } from './components/SearchBar';
import { KeyboardHelp } from './components/KeyboardHelp';
import { ImportExport } from './components/ImportExport';
import { Statistics } from './components/Statistics';
import { DifficultyRating } from './components/DifficultyRating';
import { MultipleChoice } from './components/MultipleChoice';
import { TypingPractice } from './components/TypingPractice';
import { ClozeCard } from './components/ClozeCard';
import { AchievementNotification } from './components/AchievementNotification';
import { SessionSummary } from './components/SessionSummary';
import { ColorSchemeSelector } from './components/ColorSchemeSelector';
import { StudyReminders } from './components/StudyReminders';
import { CardLayoutSettings } from './components/CardLayoutSettings';
import { calculateNextReview, isCardDue, getDueCards } from './utils/srsAlgorithm';
import { isClozeCard } from './utils/cloze';
import { applyColorScheme } from './utils/colorSchemes';
import './App.css';

function App() {
  const [theme, setTheme] = useLocalStorage('theme', 'light');
  const [colorScheme, setColorScheme] = useLocalStorage('colorScheme', 'default');
  const [showColorSchemeSelector, setShowColorSchemeSelector] = useState(false);
  const [currentDeck, setCurrentDeck] = useState('Essentials');
  const [direction, setDirection] = useState('ar');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [memorized, setMemorized] = useLocalStorage('memorized', []);
  const [favorites, setFavorites] = useLocalStorage('favorites', []);
  const [reviewData, setReviewData] = useLocalStorage('reviewData', {});
  const [showMemorizedModal, setShowMemorizedModal] = useState(false);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [showMenuSheet, setShowMenuSheet] = useState(false);
  const [showDeckManager, setShowDeckManager] = useState(false);
  const [showCardEditor, setShowCardEditor] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isShuffled, setIsShuffled] = useState(false);
  const [shuffledIndices, setShuffledIndices] = useState([]);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showStudyReminders, setShowStudyReminders] = useState(false);
  const [showCardLayoutSettings, setShowCardLayoutSettings] = useState(false);
  const [cardLayoutSettings, setCardLayoutSettings] = useLocalStorage('cardLayoutSettings', {
    cardSize: 'medium',
    fontSize: 'medium',
    cardStyle: 'default'
  });
  const [useSRS, setUseSRS] = useState(true); // Enable SRS by default
  const [studyMode, setStudyMode] = useState('flashcard'); // 'flashcard', 'quiz', or 'typing'
  const [quizScore, setQuizScore] = useState({ correct: 0, total: 0 });
  const [autoAdvance, setAutoAdvance] = useLocalStorage('autoAdvance', true);
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [sessionData, setSessionData] = useState({
    cardsReviewed: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    ratings: { again: 0, hard: 0, medium: 0, easy: 0 },
    studyMode: 'flashcard',
    deckName: 'Essentials',
    startTime: Date.now()
  });

  // Custom decks management
  const {
    customDecks,
    addDeck,
    deleteDeck,
    renameDeck,
    addCard,
    updateCard,
    deleteCard,
    importDeck,
    restoreAllDecks
  } = useCustomDecks();

  // Study timer
  const { formattedTime, start, reset } = useStudyTimer();

  // Streak tracking
  const { currentStreak, longestStreak, todayStudied, recordStudySession } = useStreaks();

  // Study goals
  const {
    dailyGoal,
    weeklyGoal,
    dailyProgress,
    weeklyProgress,
    dailyGoalMet,
    weeklyGoalMet,
    dailyProgressPercent,
    weeklyProgressPercent,
    recordCardReview,
    setDailyGoal,
    setWeeklyGoal
  } = useStudyGoals();

  // Achievements
  const {
    achievements,
    unlockedAchievements,
    newlyUnlocked,
    unlockedCount,
    totalCount,
    checkAndUnlockAchievements,
    clearNewAchievements,
    getProgress
  } = useAchievements();

  // Deck categories
  const {
    categories,
    addCategory,
    deleteCategory,
    renameCategory,
    assignDeckToCategory,
    getDeckCategory,
    getCategorizedDecks
  } = useDeckCategories();

  // Merge default decks with custom decks
  const allDecks = { ...DECKS, ...customDecks };

  // Auto-start timer when app loads
  useEffect(() => {
    start();
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.content = theme === 'dark' ? '#000000' : '#ffffff';
    }
  }, [theme]);

  // Apply color scheme
  useEffect(() => {
    applyColorScheme(colorScheme, theme);
  }, [colorScheme, theme]);

  const getCardKey = (deck, index) => `${deck}|${index}`;

  const isCardMemorized = (deck, index) => {
    return memorized.includes(getCardKey(deck, index));
  };

  const isCardFavorited = (deck, index) => {
    return favorites.includes(getCardKey(deck, index));
  };

  const toggleFavorite = () => {
    if (activeCards.length === 0) return;
    const actualIndex = activeCards[currentIndex];
    const key = getCardKey(currentDeck, actualIndex);

    if (favorites.includes(key)) {
      setFavorites(favorites.filter(k => k !== key));
    } else {
      setFavorites([...favorites, key]);
    }
  };

  const unfavoriteCard = (deck, index) => {
    const key = getCardKey(deck, index);
    setFavorites(favorites.filter(k => k !== key));
  };

  // Shuffle array using Fisher-Yates algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Filter cards based on search query
  const getFilteredCards = () => {
    if (!allDecks[currentDeck]) return [];

    let indices = allDecks[currentDeck].map((_, i) => i);

    // Filter out memorized cards if not searching
    if (!isSearchActive) {
      indices = indices.filter(i => !isCardMemorized(currentDeck, i));
    }

    // Filter by search query
    if (isSearchActive && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      indices = indices.filter(i => {
        const card = allDecks[currentDeck][i];
        const matchesText =
          card.ar.toLowerCase().includes(query) ||
          card.en.toLowerCase().includes(query);
        const matchesTags = card.tags && card.tags.some(tag =>
          tag.toLowerCase().includes(query)
        );
        return matchesText || matchesTags;
      });
    }

    // Apply shuffle if enabled
    if (isShuffled && shuffledIndices.length > 0) {
      // Map indices through shuffled order
      const indexSet = new Set(indices);
      indices = shuffledIndices.filter(i => indexSet.has(i));
    }

    return indices;
  };

  const activeCards = getFilteredCards();
  const totalCards = allDecks[currentDeck] ? allDecks[currentDeck].length : 0;
  const memorizedCount = isSearchActive
    ? totalCards - activeCards.length
    : allDecks[currentDeck]
        ? allDecks[currentDeck].filter((_, i) => isCardMemorized(currentDeck, i)).length
        : 0;

  const currentCard = activeCards.length > 0
    ? allDecks[currentDeck][activeCards[currentIndex]]
    : null;

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const flipCard = () => {
    setIsFlipped(!isFlipped);
  };

  const nextCard = () => {
    if (activeCards.length === 0) return;
    setIsFlipped(false);
    setCurrentIndex((currentIndex + 1) % activeCards.length);
  };

  const prevCard = () => {
    if (activeCards.length === 0) return;
    setIsFlipped(false);
    setCurrentIndex((currentIndex - 1 + activeCards.length) % activeCards.length);
  };

  const markMemorized = () => {
    if (activeCards.length === 0) return;
    const actualIndex = activeCards[currentIndex];
    const key = getCardKey(currentDeck, actualIndex);
    
    if (memorized.includes(key)) {
      setMemorized(memorized.filter(k => k !== key));
    } else {
      setMemorized([...memorized, key]);
      if (activeCards.length > 1) {
        setCurrentIndex(currentIndex % (activeCards.length - 1));
      } else {
        setCurrentIndex(0);
      }
    }
    setIsFlipped(false);
  };

  const unmarkCard = (deck, index) => {
    const key = getCardKey(deck, index);
    setMemorized(memorized.filter(k => k !== key));
  };

  // SRS Functions
  const handleRating = (rating) => {
    if (activeCards.length === 0) return;

    // Check if current card is a cloze card (which doesn't require flipping)
    const actualIndex = activeCards[currentIndex];
    const currentCard = allDecks[currentDeck]?.[actualIndex];
    const isClozeDeletion = currentCard && isClozeCard(currentCard);

    // Regular flashcards require flipping before rating
    if (!isClozeDeletion && !isFlipped) return;

    const key = getCardKey(currentDeck, actualIndex);
    const currentReview = reviewData[key];

    // Calculate next review
    const newReview = calculateNextReview(currentReview, rating);

    // Update review data
    setReviewData({
      ...reviewData,
      [key]: newReview
    });

    // Track session data
    const isCorrect = rating === 'easy' || rating === 'medium';
    setSessionData(prev => ({
      ...prev,
      cardsReviewed: prev.cardsReviewed + 1,
      correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
      incorrectAnswers: prev.incorrectAnswers + (isCorrect ? 0 : 1),
      ratings: {
        ...prev.ratings,
        [rating]: prev.ratings[rating] + 1
      },
      studyMode: 'flashcard',
      deckName: currentDeck
    }));

    // Record study session for streak tracking
    recordStudySession();

    // Record card review for goal tracking
    recordCardReview();

    // Check and unlock achievements
    checkAndUnlockAchievements({
      cardsReviewed: 1,
      currentStreak,
      dailyGoalMet,
      weeklyGoalMet,
      isCorrect
    });

    // Auto-advance to next card if enabled
    setIsFlipped(false);
    if (autoAdvance && activeCards.length > 1) {
      nextCard();
    }
  };

  const getNextIntervals = () => {
    if (activeCards.length === 0) return null;

    const actualIndex = activeCards[currentIndex];
    const key = getCardKey(currentDeck, actualIndex);
    const currentReview = reviewData[key];

    // Calculate intervals for each rating
    const ratings = ['again', 'hard', 'medium', 'easy'];
    const intervals = {};

    ratings.forEach(rating => {
      const review = calculateNextReview(currentReview, rating);
      intervals[rating] = review.interval;
    });

    return intervals;
  };

  const toggleStudyMode = () => {
    // Cycle through modes: flashcard -> quiz -> typing -> flashcard
    let newMode;
    if (studyMode === 'flashcard') {
      newMode = 'quiz';
    } else if (studyMode === 'quiz') {
      newMode = 'typing';
    } else {
      newMode = 'flashcard';
    }

    setStudyMode(newMode);
    setCurrentIndex(0);
    setIsFlipped(false);
    setQuizScore({ correct: 0, total: 0 });
  };

  const handleQuizAnswer = (isCorrect) => {
    setQuizScore({
      correct: quizScore.correct + (isCorrect ? 1 : 0),
      total: quizScore.total + 1
    });

    // Track session data
    setSessionData(prev => ({
      ...prev,
      cardsReviewed: prev.cardsReviewed + 1,
      correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
      incorrectAnswers: prev.incorrectAnswers + (isCorrect ? 0 : 1),
      studyMode,
      deckName: currentDeck
    }));

    // Record study session for streak tracking
    recordStudySession();

    // Record card review for goal tracking
    recordCardReview();

    // Check and unlock achievements
    checkAndUnlockAchievements({
      cardsReviewed: 1,
      currentStreak,
      dailyGoalMet,
      weeklyGoalMet,
      isCorrect
    });
  };

  const handleDeckChange = (deckName) => {
    setCurrentDeck(deckName);
    setCurrentIndex(0);
    setIsFlipped(false);
    setIsShuffled(false);
    setShuffledIndices([]);
    setQuizScore({ correct: 0, total: 0 });
  };

  const toggleShuffle = () => {
    if (!isShuffled) {
      // Enable shuffle - create shuffled indices
      const indices = allDecks[currentDeck] ? allDecks[currentDeck].map((_, i) => i) : [];
      setShuffledIndices(shuffleArray(indices));
      setIsShuffled(true);
      setCurrentIndex(0);
      setIsFlipped(false);
    } else {
      // Disable shuffle - reset to original order
      setIsShuffled(false);
      setShuffledIndices([]);
      setCurrentIndex(0);
      setIsFlipped(false);
    }
  };

  const toggleSearch = () => {
    setIsSearchActive(!isSearchActive);
    if (isSearchActive) {
      setSearchQuery('');
      setCurrentIndex(0);
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleImportDeck = (deckName, cards) => {
    importDeck(deckName, cards);
    setCurrentDeck(deckName);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleRestoreBackup = (backup) => {
    restoreAllDecks(backup.customDecks);
    setMemorized(backup.memorized);
    setFavorites(backup.favorites || []);
    setTheme(backup.theme);
    setReviewData(backup.reviewData || {});
  };

  const swipeHandlers = useSwipe(nextCard, prevCard);

  // Keyboard shortcuts (conditionally based on study mode)
  const getKeyboardShortcuts = () => {
    const commonShortcuts = {
      'Escape': () => {
        // Close any open modals
        if (showMemorizedModal) setShowMemorizedModal(false);
        else if (showFavoritesModal) setShowFavoritesModal(false);
        else if (showStatistics) setShowStatistics(false);
        else if (showMenuSheet) setShowMenuSheet(false);
        else if (showDeckManager) setShowDeckManager(false);
        else if (showCardEditor) setShowCardEditor(false);
        else if (showKeyboardHelp) setShowKeyboardHelp(false);
        else if (showImportExport) setShowImportExport(false);
        else if (isSearchActive) toggleSearch();
      },
      '?': () => {
        if (showKeyboardHelp) {
          setShowKeyboardHelp(false);
        } else if (showMenuSheet) {
          setShowMenuSheet(false);
          setShowKeyboardHelp(true);
        } else {
          setShowKeyboardHelp(true);
        }
      },
    };

    if (studyMode === 'typing') {
      // Typing mode: minimal shortcuts (typing takes priority)
      return commonShortcuts;
    } else if (studyMode === 'quiz') {
      // Quiz mode: just common shortcuts
      return commonShortcuts;
    } else {
      // Flashcard mode: full shortcuts
      return {
        ...commonShortcuts,
        'arrowright': nextCard,
        'arrowleft': prevCard,
        ' ': flipCard, // Space to flip
        'f': flipCard, // F to flip
        'm': markMemorized, // M to mark memorized (legacy support)
        'b': toggleFavorite, // B to bookmark/favorite
        's': toggleSearch, // S to toggle search
        'r': toggleShuffle, // R to shuffle/randomize
        '1': () => handleRating('again'), // SRS: Again
        '2': () => handleRating('hard'), // SRS: Hard
        '3': () => handleRating('medium'), // SRS: Good/Medium
        '4': () => handleRating('easy'), // SRS: Easy
      };
    }
  };

  const keyboardShortcuts = getKeyboardShortcuts();

  const isAnyModalOpen = showMemorizedModal || showFavoritesModal || showMenuSheet || showDeckManager || showCardEditor || showKeyboardHelp || showImportExport;
  useKeyboard(keyboardShortcuts, !isAnyModalOpen || keyboardShortcuts['Escape']);

  const layoutClasses = `app card-size-${cardLayoutSettings.cardSize} font-size-${cardLayoutSettings.fontSize} card-style-${cardLayoutSettings.cardStyle}`;

  return (
    <div className={layoutClasses}>
      <Header
        theme={theme}
        onToggleTheme={toggleTheme}
        onMenuClick={() => setShowMenuSheet(true)}
        onSearchClick={toggleSearch}
        isSearchActive={isSearchActive}
      />

      {!isSearchActive ? (
        <TopicSelector
          topics={Object.keys(allDecks)}
          currentTopic={currentDeck}
          direction={direction}
          onTopicChange={handleDeckChange}
          onDirectionChange={setDirection}
        />
      ) : (
        <div className="search-container">
          <SearchBar
            value={searchQuery}
            onChange={handleSearchChange}
            onClear={() => setSearchQuery('')}
            placeholder="Search in all decks..."
            isActive={isSearchActive}
          />
          {searchQuery && (
            <div className="search-info">
              Found {activeCards.length} card{activeCards.length !== 1 ? 's' : ''} in {currentDeck}
            </div>
          )}
        </div>
      )}

      <div className="main">
        {studyMode === 'quiz' ? (
          // Quiz Mode
          <div className="card-container">
            {activeCards.length > 0 && currentCard ? (
              <MultipleChoice
                card={currentCard}
                direction={direction}
                allCards={allDecks[currentDeck] || []}
                onAnswer={handleQuizAnswer}
                onNext={nextCard}
                counter={`${currentIndex + 1}/${activeCards.length}`}
                score={quizScore}
                autoAdvance={autoAdvance}
              />
            ) : (
              <div className="empty-state">
                🎉 Quiz completed!<br/><br/>
                Final Score: {quizScore.correct}/{quizScore.total} ({quizScore.total > 0 ? Math.round((quizScore.correct / quizScore.total) * 100) : 0}%)
              </div>
            )}
          </div>
        ) : studyMode === 'typing' ? (
          // Typing Practice Mode
          <div className="card-container">
            {activeCards.length > 0 && currentCard ? (
              <TypingPractice
                card={currentCard}
                direction={direction}
                onAnswer={handleQuizAnswer}
                onNext={nextCard}
                counter={`${currentIndex + 1}/${activeCards.length}`}
                score={quizScore}
                autoAdvance={autoAdvance}
              />
            ) : (
              <div className="empty-state">
                🎉 Typing practice completed!<br/><br/>
                Final Score: {quizScore.correct}/{quizScore.total} ({quizScore.total > 0 ? Math.round((quizScore.correct / quizScore.total) * 100) : 0}%)
              </div>
            )}
          </div>
        ) : (
          // Flashcard Mode
          <>
            <div className="card-container" {...swipeHandlers}>
              {activeCards.length > 0 && currentCard ? (
                isClozeCard(currentCard) ? (
                  <ClozeCard
                    card={currentCard}
                    direction={direction}
                    counter={`${currentIndex + 1}/${activeCards.length}`}
                    onComplete={(difficulty) => {
                      // Map cloze difficulty to SRS ratings
                      const ratingMap = {
                        'wrong': 'again',
                        'hard': 'hard',
                        'good': 'medium',
                        'easy': 'easy'
                      };
                      handleRating(ratingMap[difficulty] || 'medium');
                      if (autoAdvance) {
                        setTimeout(() => nextCard(), 300);
                      }
                    }}
                  />
                ) : (
                  <Flashcard
                    card={currentCard}
                    direction={direction}
                    isFlipped={isFlipped}
                    onFlip={flipCard}
                    counter={`${currentIndex + 1}/${activeCards.length}`}
                    isFavorited={isCardFavorited(currentDeck, activeCards[currentIndex])}
                    onToggleFavorite={toggleFavorite}
                  />
                )
              ) : (
                <div className="empty-state">
                  🎉 All cards memorized!<br/><br/>
                  Select another topic or review your memorized cards
                </div>
              )}
              <div className="swipe-hint">Swipe left/right to navigate</div>
            </div>

            <Stats
              total={totalCards}
              remaining={activeCards.length}
              memorized={memorizedCount}
              studyTime={formattedTime}
            />

            {useSRS && isFlipped ? (
              <DifficultyRating
                onRate={handleRating}
                isFlipped={isFlipped}
                nextIntervals={getNextIntervals()}
              />
            ) : (
              <BottomNav
                onPrev={prevCard}
                onNext={nextCard}
                onMemorize={markMemorized}
                disabled={activeCards.length === 0}
                isMemorized={activeCards.length > 0 && isCardMemorized(currentDeck, activeCards[currentIndex])}
              />
            )}
          </>
        )}
      </div>

      {showMemorizedModal && (
        <MemorizedModal
          memorized={memorized}
          decks={allDecks}
          onClose={() => setShowMemorizedModal(false)}
          onUnmark={unmarkCard}
        />
      )}

      {showFavoritesModal && (
        <FavoritesModal
          favorites={favorites}
          decks={allDecks}
          onClose={() => setShowFavoritesModal(false)}
          onUnfavorite={unfavoriteCard}
        />
      )}

      {showMenuSheet && (
        <MenuSheet
          onViewMemorized={() => {
            setShowMenuSheet(false);
            setShowMemorizedModal(true);
          }}
          onViewFavorites={() => {
            setShowMenuSheet(false);
            setShowFavoritesModal(true);
          }}
          onManageDecks={() => {
            setShowMenuSheet(false);
            setShowDeckManager(true);
          }}
          onManageCategories={() => {
            setShowMenuSheet(false);
            setShowCategoryManager(true);
          }}
          onEditCards={() => {
            setShowMenuSheet(false);
            setShowCardEditor(true);
          }}
          onToggleShuffle={() => {
            toggleShuffle();
            setShowMenuSheet(false);
          }}
          onToggleStudyMode={() => {
            toggleStudyMode();
            setShowMenuSheet(false);
          }}
          onToggleAutoAdvance={() => {
            setAutoAdvance(!autoAdvance);
          }}
          onShowKeyboardHelp={() => {
            setShowMenuSheet(false);
            setShowKeyboardHelp(true);
          }}
          onShowImportExport={() => {
            setShowMenuSheet(false);
            setShowImportExport(true);
          }}
          onShowStatistics={() => {
            setShowMenuSheet(false);
            setShowStatistics(true);
          }}
          onShowStudyReminders={() => {
            setShowMenuSheet(false);
            setShowStudyReminders(true);
          }}
          onShowCardLayoutSettings={() => {
            setShowMenuSheet(false);
            setShowCardLayoutSettings(true);
          }}
          onColorSchemes={() => {
            setShowMenuSheet(false);
            setShowColorSchemeSelector(true);
          }}
          onEndSession={() => {
            setShowMenuSheet(false);
            if (sessionData.cardsReviewed > 0) {
              setShowSessionSummary(true);
            } else {
              // If no cards reviewed, just reset to deck selection
              setCurrentDeck('');
              setCurrentIndex(0);
              setIsFlipped(false);
              setQuizScore({ correct: 0, total: 0 });
            }
            // Always reset session data
            setSessionData({
              cardsReviewed: 0,
              correctAnswers: 0,
              incorrectAnswers: 0,
              ratings: { again: 0, hard: 0, medium: 0, easy: 0 },
              studyMode,
              deckName: '',
              startTime: Date.now()
            });
          }}
          isShuffled={isShuffled}
          studyMode={studyMode}
          autoAdvance={autoAdvance}
          onClose={() => setShowMenuSheet(false)}
        />
      )}

      {showKeyboardHelp && (
        <KeyboardHelp onClose={() => setShowKeyboardHelp(false)} />
      )}

      {showImportExport && (
        <ImportExport
          allDecks={allDecks}
          customDecks={customDecks}
          memorized={memorized}
          favorites={favorites}
          theme={theme}
          reviewData={reviewData}
          onImportDeck={handleImportDeck}
          onRestoreBackup={handleRestoreBackup}
          onClose={() => setShowImportExport(false)}
        />
      )}

      {showStatistics && (
        <Statistics
          allDecks={allDecks}
          reviewData={reviewData}
          memorized={memorized}
          favorites={favorites}
          theme={theme}
          currentStreak={currentStreak}
          longestStreak={longestStreak}
          todayStudied={todayStudied}
          dailyGoal={dailyGoal}
          weeklyGoal={weeklyGoal}
          dailyProgress={dailyProgress}
          weeklyProgress={weeklyProgress}
          dailyGoalMet={dailyGoalMet}
          weeklyGoalMet={weeklyGoalMet}
          dailyProgressPercent={dailyProgressPercent}
          weeklyProgressPercent={weeklyProgressPercent}
          onSetDailyGoal={setDailyGoal}
          onSetWeeklyGoal={setWeeklyGoal}
          achievements={achievements}
          unlockedAchievements={unlockedAchievements}
          unlockedCount={unlockedCount}
          totalCount={totalCount}
          getProgress={getProgress}
          onClose={() => setShowStatistics(false)}
        />
      )}

      {showDeckManager && (
        <DeckManager
          allDecks={allDecks}
          customDecks={customDecks}
          onAddDeck={addDeck}
          onDeleteDeck={(deckName) => {
            deleteDeck(deckName);
            if (currentDeck === deckName) {
              setCurrentDeck('Essentials');
              setCurrentIndex(0);
            }
          }}
          onRenameDeck={(oldName, newName) => {
            const success = renameDeck(oldName, newName);
            if (success && currentDeck === oldName) {
              setCurrentDeck(newName);
            }
            return success;
          }}
          onSelectDeck={handleDeckChange}
          onClose={() => setShowDeckManager(false)}
        />
      )}

      {showCardEditor && (
        <CardEditor
          deckName={currentDeck}
          cards={allDecks[currentDeck] || []}
          allDecks={allDecks}
          isCustomDeck={!!customDecks[currentDeck]}
          onAddCard={addCard}
          onUpdateCard={updateCard}
          onDeleteCard={deleteCard}
          onBulkDelete={(indices) => {
            indices.sort((a, b) => b - a).forEach(index => {
              deleteCard(currentDeck, index);
            });
          }}
          onBulkTag={(indices, tag) => {
            indices.forEach(index => {
              const card = allDecks[currentDeck][index];
              if (card) {
                const existingTags = card.tags || [];
                if (!existingTags.includes(tag)) {
                  updateCard(currentDeck, index, {
                    ...card,
                    tags: [...existingTags, tag]
                  });
                }
              }
            });
          }}
          onBulkMoveToDeck={(indices, targetDeck) => {
            const cardsToMove = indices.map(i => allDecks[currentDeck][i]);
            cardsToMove.forEach(card => {
              if (card) addCard(targetDeck, card);
            });
            indices.sort((a, b) => b - a).forEach(index => {
              deleteCard(currentDeck, index);
            });
          }}
          onClose={() => setShowCardEditor(false)}
        />
      )}

      {showCategoryManager && (
        <CategoryManager
          categories={categories}
          allDecks={allDecks}
          deckCategoryMap={Object.keys(allDecks).reduce((map, deckName) => {
            map[deckName] = getDeckCategory(deckName);
            return map;
          }, {})}
          onAddCategory={addCategory}
          onDeleteCategory={deleteCategory}
          onRenameCategory={renameCategory}
          onAssignDeck={assignDeckToCategory}
          getCategorizedDecks={getCategorizedDecks}
          onClose={() => setShowCategoryManager(false)}
        />
      )}

      {/* Session Summary */}
      {showSessionSummary && (
        <SessionSummary
          sessionData={sessionData}
          studyTime={formattedTime}
          onClose={() => {
            setShowSessionSummary(false);
            // End session - go back to deck selection
            setCurrentDeck('');
            setCurrentIndex(0);
            setIsFlipped(false);
            setQuizScore({ correct: 0, total: 0 });
            // Show statistics
            setShowStatistics(true);
          }}
          onContinue={() => {
            setShowSessionSummary(false);
            // Reset session data but continue studying
            setCurrentIndex(0);
            setIsFlipped(false);
            setQuizScore({ correct: 0, total: 0 });
            setSessionData({
              cardsReviewed: 0,
              correctAnswers: 0,
              incorrectAnswers: 0,
              ratings: { again: 0, hard: 0, medium: 0, easy: 0 },
              studyMode,
              deckName: currentDeck,
              startTime: Date.now()
            });
          }}
        />
      )}

      {/* Color Scheme Selector */}
      {showColorSchemeSelector && (
        <ColorSchemeSelector
          currentScheme={colorScheme}
          onSelectScheme={setColorScheme}
          onClose={() => setShowColorSchemeSelector(false)}
        />
      )}

      {/* Study Reminders */}
      {showStudyReminders && (
        <StudyReminders
          onClose={() => setShowStudyReminders(false)}
        />
      )}

      {/* Card Layout Settings */}
      {showCardLayoutSettings && (
        <CardLayoutSettings
          settings={cardLayoutSettings}
          onUpdateSettings={setCardLayoutSettings}
          onClose={() => setShowCardLayoutSettings(false)}
        />
      )}

      {/* Achievement Notifications */}
      <AchievementNotification
        achievements={newlyUnlocked}
        onClose={clearNewAchievements}
      />
    </div>
  );
}

export default App;
